import { useEffect, useRef } from "react";
import { saveDraft, loadDraft, clearDraft } from "@/lib/draftStorage";

/* Global counter of open forms — used by useDeferredReload to know if it's
 * safe to reload silently. Any mounted form using this hook bumps the counter,
 * regardless of whether the draft is dirty (modal-open == user is working). */
let openFormCount = 0;
const listeners = new Set();

function notify() {
  for (const l of listeners) {
    try {
      l(openFormCount);
    } catch {}
  }
}

export function getOpenFormCount() {
  return openFormCount;
}

export function subscribeOpenFormCount(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * useDraftAutosave — persists form state to localStorage every `intervalMs`,
 * exposes the previously saved draft on mount, and registers the form as "open"
 * so the deferred-reload manager won't reload while the user is mid-capture.
 *
 * @param {string} formKey — stable identifier (e.g. "newTrip", `editTrip:${id}`)
 * @param {object} state — current form state to persist
 * @param {object} opts
 * @param {number} opts.intervalMs — autosave cadence (default 2000)
 * @param {boolean} opts.enabled — disable persistence (default true)
 * @param {function} opts.isEmpty — predicate; if true the autosaver clears draft
 *                                  instead of writing (default: false)
 * @returns {{ savedDraft: object|null, clear: function }}
 */
export function useDraftAutosave(formKey, state, opts = {}) {
  const { intervalMs = 2000, enabled = true, isEmpty } = opts;
  const stateRef = useRef(state);
  stateRef.current = state;

  // Snapshot of pre-existing draft, captured once at mount.
  const initialDraftRef = useRef(undefined);
  if (initialDraftRef.current === undefined) {
    initialDraftRef.current = loadDraft(formKey);
  }

  // Bump global open-form counter while mounted.
  useEffect(() => {
    if (!enabled) return undefined;
    openFormCount += 1;
    notify();
    return () => {
      openFormCount = Math.max(0, openFormCount - 1);
      notify();
    };
  }, [enabled]);

  // Periodic write.
  // Sentinel short-circuit: callers that use this hook purely as a
  // "modal-open lock" (FlightDetailModal, etc.) pass `state: null` AND
  // `isEmpty: () => true`. In that case there is no draft to persist and no
  // useful clear to perform — running setInterval just to call clearDraft on
  // a key that was never written wastes a tick + a localStorage touch every
  // 2s for the entire modal lifetime. Skip the interval entirely.
  const _isLockOnly = (state === null || state === undefined) && isEmpty && isEmpty(null) === true;
  useEffect(() => {
    if (!enabled) return undefined;
    if (_isLockOnly) return undefined;
    const id = setInterval(() => {
      const cur = stateRef.current;
      if (isEmpty && isEmpty(cur)) {
        clearDraft(formKey);
      } else if (cur !== undefined && cur !== null) {
        saveDraft(formKey, cur);
      }
    }, intervalMs);
    return () => clearInterval(id);
  }, [formKey, enabled, intervalMs, isEmpty, _isLockOnly]);

  return {
    savedDraft: initialDraftRef.current,
    clear: () => clearDraft(formKey),
  };
}
